import React, { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'

type QrPoint = {
  id: string
  code: string
  label: string | null
  area_type: string | null
  status: string
}

type ReqRow = {
  id?: string
  qr_point_id: string
  enforcement: 'optional' | 'reminder' | 'required'
}

interface JobQrRequirementsPickerProps {
  jobId: string
  propertyId?: string | null
}

export function JobQrRequirementsPicker({ jobId, propertyId }: JobQrRequirementsPickerProps) {
  const [points, setPoints] = useState<QrPoint[]>([])
  const [reqs, setReqs] = useState<ReqRow[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = async () => {
    setError(null)
    try {
      if (propertyId) {
        const { data, error: pErr } = await supabase
          .from('qr_points')
          .select('id, code, label, area_type, status')
          .eq('property_id', propertyId)
          .eq('status', 'active')
          .order('label')
        if (pErr) throw pErr
        setPoints(data || [])
      } else {
        setPoints([])
      }
      const { data: r, error: rErr } = await supabase
        .from('job_qr_requirements')
        .select('id, qr_point_id, enforcement')
        .eq('job_id', jobId)
      if (rErr) throw rErr
      setReqs(r || [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load QR requirements')
    }
  }

  useEffect(() => {
    if (jobId) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, propertyId])

  const upsert = async (pointId: string, enforcement: ReqRow['enforcement'] | 'off') => {
    setSaving(true)
    setError(null)
    try {
      const existing = reqs.find((r) => r.qr_point_id === pointId)
      if (enforcement === 'off') {
        if (existing?.id) {
          const { error } = await supabase.from('job_qr_requirements').delete().eq('id', existing.id)
          if (error) throw error
        }
      } else if (existing?.id) {
        const { error } = await supabase
          .from('job_qr_requirements')
          .update({ enforcement })
          .eq('id', existing.id)
        if (error) throw error
      } else {
        const { data: auth } = await supabase.auth.getUser()
        const { error } = await supabase.from('job_qr_requirements').insert({
          job_id: jobId,
          qr_point_id: pointId,
          enforcement,
          created_by: auth.user?.id || null,
        })
        if (error) throw error
      }
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  if (!propertyId) {
    return <p className="text-sm text-gray-500">Select a property to attach QR requirements.</p>
  }

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">QR scan requirements</h3>
        <p className="text-xs text-gray-500">
          Optional = track only · Reminder = popup at complete · Required = blocks complete
        </p>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {points.length === 0 ? (
        <p className="text-sm text-gray-500">No active QR points on this property yet.</p>
      ) : (
        <div className="space-y-2">
          {points.map((p) => {
            const current = reqs.find((r) => r.qr_point_id === p.id)?.enforcement || 'off'
            return (
              <div
                key={p.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">{p.label || p.code}</p>
                  <p className="text-xs text-gray-500">
                    {p.area_type || 'area'} · {p.code}
                  </p>
                </div>
                <select
                  className="text-sm border border-gray-300 rounded-lg px-2 py-1"
                  value={current}
                  disabled={saving}
                  onChange={(e) => upsert(p.id, e.target.value as any)}
                >
                  <option value="off">Off</option>
                  <option value="optional">Optional</option>
                  <option value="reminder">Reminder</option>
                  <option value="required">Required</option>
                </select>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
